/// <reference types="google.maps" />

'use client'

import { useCallback, useEffect, useState } from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import type { MapLocatorClusteringSettings } from 'ecosystem'
import { createClusterRenderer } from './clusterRenderer'

type MarkerInstance = google.maps.marker.AdvancedMarkerElement

/**
 * Bridges React-rendered Advanced Markers into a MarkerClusterer instance.
 *
 * Markers register themselves through `setMarkerRef` as they mount; the clusterer
 * is rebuilt whenever that set changes (filters, viewport culling), which keeps
 * cluster counts honest instead of leaving hidden markers inside a cluster.
 */
export const useClusteredMarkers = (clustering: MapLocatorClusteringSettings) => {
  const map = useMap()
  /**
   * State, not a ref: the effect that fills the clusterer must re-run when the
   * instance is replaced. With a ref its identity is invisible to React, so a
   * rebuilt clusterer stayed empty while the old one had already detached every
   * marker — the whole map went blank on any appearance change.
   */
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null)
  const [markers, setMarkers] = useState<Record<string, MarkerInstance>>({})

  const { enabled, maxZoom, radius, shape, color, textColor } = clustering
  const { borderColor, borderWidth, scaleWithCount } = clustering

  useEffect(() => {
    if (!map || !enabled) {
      return
    }

    const instance = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({ maxZoom, radius }),
      renderer: createClusterRenderer({
        enabled,
        shape,
        color,
        textColor,
        borderColor,
        borderWidth,
        scaleWithCount
      })
    })

    setClusterer(instance)

    return () => {
      instance.clearMarkers()
      instance.setMap(null)
      setClusterer(null)
    }
    // The badge appearance is baked into the renderer, so a change rebuilds the clusterer
  }, [
    map,
    enabled,
    maxZoom,
    radius,
    shape,
    color,
    textColor,
    borderColor,
    borderWidth,
    scaleWithCount
  ])

  useEffect(() => {
    if (!clusterer) {
      return
    }

    clusterer.clearMarkers(true)
    clusterer.addMarkers(Object.values(markers))
  }, [clusterer, markers])

  const setMarkerRef = useCallback(
    (marker: MarkerInstance | null, key: string) => {
      if (!enabled) {
        return
      }

      setMarkers((current) => {
        if ((marker && current[key] === marker) || (!marker && !(key in current))) {
          return current
        }

        if (marker) {
          return { ...current, [key]: marker }
        }

        const { [key]: _removed, ...rest } = current

        return rest
      })
    },
    [enabled]
  )

  return { setMarkerRef, clusteringEnabled: enabled }
}
